<template>
  <div class="full-width fill-height">
    <div class="fill-height full-width d-flex flex-column">
      <div class="header d-flex flex-shrink-0 full-width">
        <div class="header-c1 d-inline-flex align-center fill-height flex-grow-1 flex-shrink-0">
          <div class="d-inline-flex header-body" v-if="singleOtherParticipant">
            <v-btn v-if="$vuetify.breakpoint.xsOnly" icon @click.stop="$emit('back')">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <Avatar v-if="singleOtherParticipant" :userId="singleOtherParticipant.uid" size="36" class="mr-3" />
            <div class="d-inline-flex flex-column">
              <div class="header-body-title">
                {{ singleOtherParticipant.displayName }}
              </div>
              <div class="header-body-subtitle">
                <span>
                  {{ singleOtherUser && singleOtherUser.online ? $t("chat.online") : $t("chat.offline") }}
                </span>
                <span v-if="chiefComplaint"> | {{ chiefComplaint }} </span>
              </div>
            </div>
          </div>
          <div class="ml-4">
            <v-progress-circular v-if="messagesLoading" indeterminate size="15" width="2" color="primary" />
          </div>
        </div>
        <div class="header-c2 d-inline-flex align-center fill-height pr-6">
          <v-btn v-if="!isArchived && role === 'practitioner'" icon @click="archiveDialog = true">
            <img :src="require('@/assets/chat/archive.svg')" alt="archive.svg" />
          </v-btn>
        </div>
      </div>
      <div v-chat="{ always: false }" @chatTopReached="prevPage" class="chat flex-grow-1" ref="chatContainer">
        <Preview v-if="!messagesLoading && !messagesComp.length" :text="$t('chat.preview.noMessages')" />
        <Message v-else v-for="msg in messagesComp" :key="msg.index" :message="msg" />
      </div>
      <div v-if="!isArchived" class="input flex-shrink-0 d-flex flex-column">
        <div v-if="files" class="mb-1 d-flex flex-wrap">
          <v-chip v-for="file in files" :key="file.name" color="primary" dark label small class="mr-2 mb-1">
            {{ file.name }}
          </v-chip>
        </div>
        <div class="input-body d-flex">
          <div class="flex-grow-1">
            <v-textarea
              v-model="message"
              class="input-body-textarea"
              solo
              flat
              auto-grow
              hide-details
              dense
              rows="1"
              :placeholder="$t('chat.inputPlaceholder')"
              row-height="20"
              v-on:keydown.enter.exact.prevent
              v-on:keyup.enter.exact="send"
            />
          </div>
          <div class="input-body-actions d-flex flex-column flex-nowrap">
            <v-spacer />
            <div class="input-body-actions-buttons">
              <input v-show="false" type="file" ref="uploads" name="uploads" multiple @change="updateFileLists" />
              <v-btn icon color="primary" @click="openFileUploads">
                <v-icon>mdi-paperclip</v-icon>
              </v-btn>
              <v-btn icon color="primary" @click="send">
                <v-progress-circular v-if="sendLoading" indeterminate color="primary" size="23" width="3" />
                <v-icon v-else>mdi-send</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Confirm
      :dialog="archiveDialog"
      :title="$t('chat.archiveDialog.title')"
      :text="$t('chat.archiveDialog.text')"
      :confirmText="$t('chat.archiveDialog.confirm')"
      :cancelText="$t('general.dialog.cancel')"
      :loading="archiveLoading"
      confirm-color="primary"
      @toggleDialog="archiveDialog = false"
      @confirm="archive"
    />
  </div>
</template>

<script>
import { DateTime } from "luxon";
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";

import Avatar from "@/components/Avatar/Index";
import Confirm from "@/components/shared/Confirm";
import { snackBarEventBus, snackBarEventName } from "@/eventBuses/snackBar.eventBus";
import { scrollToBottom } from "@/utils/scrollToBottom";

import Message from "./Message.vue";
import Preview from "./Preview.vue";

export default Vue.extend({
  name: "ChatChannel",
  props: ["channel", "active"],
  components: {
    Message,
    Preview,
    Confirm,
    Avatar,
  },
  data: () => ({
    message: "",
    prevPageLoading: false,
    messages: null,
    messagesWrapper: null,
    channelAvatar: "https://cdn.vuetifyjs.com/images/john.jpg",
    otherMembers: [],
    otherUsers: [],
    files: null,
    sendLoading: false,
    messagesLoading: false,
    archiveDialog: false,
    archiveLoading: false,
  }),
  methods: {
    ...mapActions("chat", ["connect", "getMember"]),
    openFileUploads() {
      this.$refs.uploads.click();
    },
    updateFileLists() {
      this.files = [];
      for (const file of this.$refs.uploads.files) {
        this.files.push(file);
      }
    },
    async getMembers() {
      if (!this.channel) {
        return;
      }
      const members = await this.channel.getMembers();
      this.otherMembers = members.filter((member) => member.identity !== this.memberId);
      await Promise.all(this.otherMembers.map((member) => this.getMember(member.identity)));
      this.otherUsers = await Promise.all(this.otherMembers.map((member) => member.getUser()));
    },
    async getMessages() {
      if (!this.channel) {
        return;
      }
      this.messagesLoading = true;
      this.messagesWrapper = await this.channel.getMessages();
      this.messages = this.messagesWrapper.items;
      this.messagesLoading = false;
      await this.consumeAllMessages();
    },
    async addMessage(message) {
      if (this.messages.findIndex((item) => item.index === message.index) === -1) {
        this.messages.push(message);
        await this.consumeAllMessages();
      }
      this.$emit("newMessage");
    },
    async consumeAllMessages() {
      if (!this.active) {
        return;
      }
      Vue.set(this.channel, "unconsumedMessagesCount", 0);
      await this.channel.setAllMessagesConsumed();
      Vue.set(this.channel, "unconsumedMessagesCount", 0);
    },
    scroll() {
      let element = this.$refs.chatContainer;
      scrollToBottom(element, false);
    },
    checkScroll() {
      let el = this.$refs.chatContainer;
      const scrolled = el.scrollTop + el.clientHeight + 1 < el.scrollHeight;
      if (scrolled && el.scrollTop < el.scrollHeight / 10) {
        el.dispatchEvent(new Event("chatTopReached"));
      }
    },
    async prevPage() {
      if (!this.messagesWrapper.hasPrevPage || this.prevPageLoading) {
        return;
      }
      this.prevPageLoading = true;
      this.messagesWrapper = await this.messagesWrapper.prevPage();
      this.prevMessages(this.messagesWrapper.items);
      this.prevPageLoading = false;
      Vue.nextTick(this.checkScroll);
    },
    prevMessages(messages) {
      messages.reverse().forEach((item) => this.messages.unshift(item));
    },
    async send() {
      this.sendLoading = true;
      const text = this.message.trim();
      this.message = "";
      if (text !== "") {
        await this.channel.sendMessage(text);
      }
      if (this.files && this.files.length) {
        while (this.files.length) {
          const file = this.files[0];
          const formData = new FormData();
          formData.append("file", file);
          await this.channel.sendMessage(formData);
          this.files.shift();
        }
      }
      this.sendLoading = false;
    },
    async archive() {
      this.archiveLoading = true;
      try {
        await this.channel.updateAttributes({
          ...this.channel.attributes,
          archive: true,
        });
        this.archiveDialog = false;
      } catch (err) {
        snackBarEventBus.$emit(snackBarEventName, { message: err.message || err, type: "error" });
      }
      this.archiveLoading = false;
    },
  },
  computed: {
    ...mapGetters("chat", ["token", "memberId", "participants"]),
    ...mapGetters(["role"]),
    messagesComp() {
      if (!this.messages || this.messages.length === 0) {
        return [];
      }
      const messages = this.messages.map((message) => {
        const my = this.memberId === message.author;
        const author = my ? null : this.participants[message.author];
        const name = author ? author.displayName : null;
        const time = DateTime.fromJSDate(message.dateCreated).toFormat("t");
        return {
          index: message.index,
          body: message.body,
          author: message.author,
          dateCreated: message.dateCreated,
          time,
          my,
          name,
          raw: message,
        };
      });
      let currAuthor = null;
      for (let i = 0; i < messages.length; i++) {
        if (messages[i].author !== currAuthor) {
          messages[i].head = true;
          currAuthor = messages[i].author;
          if (i > 0) {
            messages[i - 1].last = true;
          }
        } else if (
          i > 0 &&
          DateTime.fromJSDate(messages[i].dateCreated)
            .diff(DateTime.fromJSDate(messages[i - 1].dateCreated), "minutes")
            .as("minutes") > 10
        ) {
          messages[i].head = true;
          messages[i - 1].last = true;
        }
      }
      messages[messages.length - 1].last = true;
      return messages;
    },
    singleOtherParticipant() {
      if (this.otherMembers.length === 1) {
        return this.participants[this.otherMembers[0].identity];
      }
      return null;
    },
    singleOtherUser() {
      if (this.otherUsers.length === 1) {
        return this.otherUsers[0];
      }
      return null;
    },
    fileNames() {
      return this.files.map((file) => file.name);
    },
    chiefComplaint() {
      const val = this.channel.attributes?.chiefComplaint;
      return this.$t("general.chiefComplaints")[val] || val || undefined;
    },
    isArchived() {
      return this.channel?.attributes?.archive === true;
    },
  },
  async mounted() {
    await Promise.all([this.getMembers(), this.getMessages()]);
    this.channel.on("messageAdded", this.addMessage);
    this.channel.on("memberJoined", this.getMembers);
    this.channel.on("memberLeft", this.getMembers);
    this.channel.on("memberUpdated", this.getMembers);
  },
  watch: {
    async active(val) {
      if (val) {
        this.scroll();
        await this.consumeAllMessages();
        this.scroll();
      }
    },
  },
});
</script>

<style scoped lang="scss">
.header {
  height: 75px;
  &-c1 {
    overflow: hidden;
  }
  &-body {
    margin-left: 25px;
    &-title {
      font-style: normal;
      font-weight: 500;
      font-size: 16px;
      line-height: 20px;
    }
    &-subtitle {
      font-style: normal;
      font-weight: normal;
      font-size: 12px;
      line-height: 20px;
      color: #000000;
      opacity: 0.46;
    }
  }
}
.chat {
  border-bottom: 1px solid #efeff0;
  border-top: 1px solid #efeff0;
  overflow-y: scroll;
  overflow-x: hidden;
  padding: 22px;
  scroll-padding: 22px;
}
.input {
  padding: 10px;
  min-height: 61px;
  &-body {
    border: 1px solid #d2d3e1;
    box-sizing: border-box;
    border-radius: 5px;
    overflow: hidden;
    &-textarea {
      margin-bottom: -25px;
    }
    &-actions {
      width: 80px;
      &-buttons {
        margin-bottom: 2px;
      }
    }
  }
}
.hidden {
  display: none !important;
}
</style>
